import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useState } from 'react';
import { AssignmentsSideBarFilter } from '../../education/assignments/side-bar-filter/assignments-side-bar-filter';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDate } from '../../../../utils/date-format';
import { ChecksSideBarFilter } from './side-bar/checks-side-bar-filter';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { getReportCheckOperation } from './operations/get-report-check-operation';
import { CheckType } from '../../../../api/types/reports/accountant/check.type';
import { ChecksSideBarFilterType } from './side-bar/checks-side-bar-filter.type';
import { CheckStatusType } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { UpdateStatusCheckDialog } from './update-status-check/update-status-check-dialog';

export const ReportChecks = () => {
  const [data, setData] = useState<CheckType[]>();
  const [loading, setLoading] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<CheckType | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const [filters, setFilters] = useState<ChecksSideBarFilterType>({
    branchIds: [],
    checkRepaymentStartDate: null,
    checkRepaymentEndDate: null,
    paymentCollectionEndDate: null,
    paymentCollectionStartDate: null,
    checkStatuses: [],
  });

  const fetchReportData = async (filters: ChecksSideBarFilterType) => {
    await getReportCheckOperation(filters, setData, setLoading);
  };

  const getStatusSeverity = (
    status: CheckStatusType,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case CheckStatusType.CLEARED:
        return 'success';
      case CheckStatusType.DEPOSITED:
        return 'info';
      case CheckStatusType.PENDING:
        return 'warning';
      case CheckStatusType.BOUNCED:
      case CheckStatusType.CANCELLED:
      case CheckStatusType.EXPIRED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: CheckStatusType): string => {
    switch (status) {
      case CheckStatusType.PENDING:
        return 'ממתין להפקדה';
      case CheckStatusType.DEPOSITED:
        return 'הופקד';
      case CheckStatusType.CLEARED:
        return 'פורע';
      case CheckStatusType.BOUNCED:
        return 'נדחה';
      case CheckStatusType.CANCELLED:
        return 'בוטל';
      case CheckStatusType.EXPIRED:
        return 'פג תוקף';
      default:
        return 'לא ידוע';
    }
  };

  const handleEditStatus = (check: CheckType) => {
    setSelectedCheck(check);
    setShowUpdateDialog(true);
  };

  const handleDialogClose = () => {
    setShowUpdateDialog(false);
    setSelectedCheck(null);
  };

  const handleStatusUpdated = () => {
    fetchReportData(filters);
    handleDialogClose();
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח המחאות"
        description="דו״ח זה מציג מידע אודות כלל ההמחאות של האירגון. הדו״ח מאפשר מעקב אחר המחאות לפי הסטטוסים השונים."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר המחאות לפי הסטטוסים השונים' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <ChecksSideBarFilter onSearch={fetchReportData} />
        </div>

        {/* Table Section */}
        <div style={{ flex: 1 }}>
          <DataTable
            value={data}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="id"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="assignments-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="paymentReceiptDate"
              header="תאריך קליטת תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.paymentReceiptDate)}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="bankNumber"
              header="בנק"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountNumber"
              header="חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="branchNumber"
              header="סניף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkNumber"
              header="מס המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="paymentAmount"
              header="סכום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkPaymentDate"
              header="תאריך פירעון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.checkPaymentDate)}
            />
            <Column
              field="checkStatus"
              header="סטטוס המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    outlined
                    severity="secondary"
                    onClick={() => handleEditStatus(rowData)}
                    tooltip="עריכת סטטוס"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Tag
                    style={{ minWidth: '100px' }}
                    value={getStatusLabel(rowData.checkStatus)}
                    severity={getStatusSeverity(rowData.checkStatus)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </div>

      {showUpdateDialog && selectedCheck && (
        <UpdateStatusCheckDialog
          visible={showUpdateDialog}
          onHide={handleDialogClose}
          check={selectedCheck}
          onStatusUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
};
